import React, { useMemo } from 'react';
import type { StructureRef } from '../../utils/proposalStructureRefs';
import {
  resolveStructureMentionDisplay,
  splitMessageWithStructureMentions,
} from '../../utils/proposalStructureMentions';

export type StructuresByDealResolver = (
  dealId: string,
) => StructureRef[] | undefined;

interface ProposalStructureMentionTextProps {
  message: string;
  /** Deal id for the current proposal / project (fallback structure list). */
  defaultDealId?: string;
  /** Primary deal structures (current proposal / project). */
  defaultStructures?: StructureRef[];
  structuresByDealId?: Map<string, StructureRef[]>;
  resolveStructuresForDeal?: StructuresByDealResolver;
  className?: string;
}

function structuresForDeal(
  dealId: string,
  defaultDealId: string | undefined,
  defaultStructures: StructureRef[] | undefined,
  map: Map<string, StructureRef[]> | undefined,
  resolve: StructuresByDealResolver | undefined,
): StructureRef[] | undefined {
  if (map?.has(dealId)) return map.get(dealId);
  const resolved = resolve?.(dealId);
  if (resolved?.length) return resolved;
  if (defaultDealId && dealId === defaultDealId) return defaultStructures;
  return defaultStructures;
}

const ProposalStructureMentionText: React.FC<ProposalStructureMentionTextProps> = ({
  message,
  defaultDealId,
  defaultStructures,
  structuresByDealId,
  resolveStructuresForDeal,
  className,
}) => {
  const segments = useMemo(() => splitMessageWithStructureMentions(message), [message]);

  if (!message) return null;

  return (
    <span className={className}>
      {segments.map((seg, i) => {
        if (seg.kind === 'text') {
          return (
            <React.Fragment key={i}>
              {seg.text.split('\n').map((line, li, arr) => (
                <React.Fragment key={li}>
                  {line}
                  {li < arr.length - 1 && <br />}
                </React.Fragment>
              ))}
            </React.Fragment>
          );
        }
        const list = structuresForDeal(
          seg.token.dealId,
          defaultDealId,
          defaultStructures,
          structuresByDealId,
          resolveStructuresForDeal,
        );
        const { displayName, hover } = resolveStructureMentionDisplay(seg.token, list);
        return (
          <span
            key={i}
            className="por-structure-mention"
            title={hover}
          >
            {displayName}
          </span>
        );
      })}
    </span>
  );
};

export default ProposalStructureMentionText;
